// Tabella delle utenze non predefinite di un fornitore: agenzie assegnate,
// riprova dell'accesso, eliminazione. Estratta da UtenzeFornitore.tsx per tenere la
// pagina leggibile, come InvioPraticheTabella.tsx per la console invii.
import { Fragment, useState } from "react";
import { KeyRound, X } from "lucide-react";
import { adminApi } from "@/api/admin";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/lib/dates";
import type { LookupItem, UtenzaCatalogo } from "@/types/admin";
import type { ProvaAccessoStato } from "@/types";
import { Autocomplete } from "./Autocomplete";

const MODALITA_CUSTODITE = "custodite";
const STATO_DA_RIPRISTINARE = "da_ripristinare";

// Lo stato non si legge dal solo colore: la parola sta nel badge, il motivo accanto
// (regola a11y "color-not-only", come StatoUtenzeStrip lato agenzia). Le credenziali
// mai inserite non sono un guasto — nessun rosso, nessun ambra: tono neutro e icona.
const STATO: Record<string, { label: string; className: string; Icona?: typeof KeyRound }> = {
  attiva: { label: "attiva", className: "border-emerald-400 text-emerald-700" },
  sessione_scaduta: { label: "sessione scaduta", className: "border-amber-400 text-amber-700" },
  da_ripristinare: { label: "da ripristinare", className: "border-red-300 text-red-600" },
  da_configurare: {
    label: "credenziali da inserire", className: "border-border text-muted-foreground", Icona: KeyRound,
  },
};

export function StatoUtenza({ stato, motivo }: { stato: string; motivo: string | null }) {
  const s = STATO[stato] ?? { label: stato, className: "border-border text-muted-foreground" };
  const Icona = s.Icona;
  return (
    <div className="flex flex-col items-start gap-1">
      <Badge variant="outline" className={`whitespace-nowrap gap-1 ${s.className}`}>
        {Icona && <Icona size={12} aria-hidden="true" />}
        {s.label}
      </Badge>
      {motivo && <span className="text-xs text-muted-foreground">· {motivo}</span>}
    </div>
  );
}

/** Agenzie assegnate a un'utenza, come chip con la «×» per toglierle. */
export function ChipAgenzie({ agenzie, onRimuovi }: {
  agenzie: { espo_account_id: string; nome: string }[];
  onRimuovi: (espo_account_id: string, nome: string) => void;
}) {
  if (agenzie.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {agenzie.map((a) => (
        <span key={a.espo_account_id}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 pl-2.5 pr-1 py-0.5 text-xs">
          <span>{a.nome}</span>
          <button type="button"
                  aria-label={`Scollega ${a.nome}`}
                  title={`Scollega ${a.nome}`}
                  onClick={() => onRimuovi(a.espo_account_id, a.nome)}
                  className="rounded-full p-0.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 cursor-pointer">
            <X size={12} />
          </button>
        </span>
      ))}
    </div>
  );
}

/**
 * Un'agenzia in più su questa utenza. Pannello in linea e non dialogo: l'autocomplete
 * ha bisogno di spazio e di una tendina, e un dialogo per un solo campo sarebbe
 * sovradimensionato (progressive-disclosure).
 */
export function FormAssegna({ utenza, onFatto }: { utenza: UtenzaCatalogo; onFatto: () => Promise<void> }) {
  const [scelta, setScelta] = useState<LookupItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function assegna() {
    if (!scelta) return;
    setBusy(true);
    setErr(null);
    try {
      await adminApi.utenzeCollegaAgenzia(utenza.id, scelta.id);
      setScelta(null);
      await onFatto();
    } catch {
      setErr("Collegamento non riuscito.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-end gap-2">
      <div className="w-full sm:w-80">
        <Autocomplete
          id={`assegna-${utenza.id}`}
          label="Cerca l'agenzia"
          placeholder="Nome dell'agenzia"
          value={scelta}
          onChange={setScelta}
          fetchItems={(q) => adminApi.utenzeAgenzie(q)}
        />
      </div>
      <Button size="sm" disabled={!scelta || busy} onClick={assegna}>Assegna</Button>
      {err && <p className="text-sm text-destructive">{err}</p>}
    </div>
  );
}

/** Il conteggio cambia la frase: «Le 1 agenzie» non si può leggere. */
function messaggioElimina(n: number): string {
  if (n === 0) {
    return "Nessuna agenzia è collegata a questa utenza: le credenziali verranno cancellate. Confermi?";
  }
  if (n === 1) {
    return "L'agenzia collegata resterà senza utenza A2A e le credenziali verranno cancellate. Confermi?";
  }
  return `Le ${n} agenzie collegate resteranno senza utenza A2A e le credenziali verranno cancellate. Confermi?`;
}

export function UtenzeFornitoreTabella({ utenze, onRicarica, onProva }: {
  utenze: UtenzaCatalogo[];
  onRicarica: () => Promise<void>;
  onProva: (jobId: string) => Promise<ProvaAccessoStato>;
}) {
  const [assegnaId, setAssegnaId] = useState<number | null>(null);
  const [daEliminare, setDaEliminare] = useState<UtenzaCatalogo | null>(null);
  const [daTogliere, setDaTogliere] = useState<{ u: UtenzaCatalogo; acc: string; nome: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [riprovaId, setRiprovaId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Errore delle azioni che vivono dentro un dialogo: sopra la tabella finirebbe
  // sotto la tendina scura e l'admin non lo vedrebbe mai.
  const [errDialogo, setErrDialogo] = useState<string | null>(null);

  async function riprova(u: UtenzaCatalogo) {
    setErr(null);
    setRiprovaId(u.id);
    try {
      const { job_id } = await adminApi.utenzeRiprova(u.id);
      await onProva(job_id);
    } catch {
      setErr("Non si è potuto riprovare l'accesso adesso.");
    } finally {
      setRiprovaId(null);
    }
  }

  async function elimina() {
    if (!daEliminare) return;
    setBusy(true);
    setErrDialogo(null);
    try {
      await adminApi.utenzeElimina(daEliminare.id);
      setDaEliminare(null);
      await onRicarica();
    } catch {
      // il dialogo resta aperto: l'utenza non è stata eliminata, si riprova o si annulla
      setErrDialogo("Eliminazione non riuscita.");
    } finally {
      setBusy(false);
    }
  }

  async function togli() {
    if (!daTogliere) return;
    setBusy(true);
    setErrDialogo(null);
    try {
      await adminApi.utenzeScollegaAgenzia(daTogliere.u.id, daTogliere.acc);
      setDaTogliere(null);
      await onRicarica();
    } catch {
      setErrDialogo("Non si è potuto scollegare l'agenzia.");
    } finally {
      setBusy(false);
    }
  }

  function apriElimina(u: UtenzaCatalogo) {
    setErrDialogo(null);
    setDaEliminare(u);
  }

  return (
    <>
      {err && <p className="text-sm text-destructive mb-2">{err}</p>}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              <th className="text-left py-2.5 px-4 font-medium">Utenza</th>
              <th className="text-left py-2.5 px-4 font-medium">Modalità</th>
              <th className="text-left py-2.5 px-4 font-medium">Stato</th>
              <th className="text-left py-2.5 px-4 font-medium">Agenzie</th>
              <th className="text-left py-2.5 px-4 font-medium">Ultimo accesso</th>
              <th className="text-right py-2.5 px-4 font-medium">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {utenze.map((u) => (
              <Fragment key={u.id}>
                <tr className="border-t border-border align-top">
                  <td className="py-2.5 px-4 text-foreground font-medium">{u.username}</td>
                  <td className="py-2.5 px-4 text-muted-foreground">{u.modalita.replace("_", " ")}</td>
                  <td className="py-2.5 px-4"><StatoUtenza stato={u.stato} motivo={u.motivo_stato} /></td>
                  <td className="py-2.5 px-4">
                    <ChipAgenzie agenzie={u.agenzie}
                                 onRimuovi={(acc, nome) => { setErrDialogo(null); setDaTogliere({ u, acc, nome }); }} />
                  </td>
                  <td className="py-2.5 px-4 text-muted-foreground tabular-nums whitespace-nowrap">
                    {formatDateTime(u.ultimo_accesso_ok_at)}
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <Button size="sm" variant="outline"
                              onClick={() => setAssegnaId((id) => (id === u.id ? null : u.id))}>
                        Assegna agenzia
                      </Button>
                      {u.modalita === MODALITA_CUSTODITE && u.stato === STATO_DA_RIPRISTINARE && (
                        <Button size="sm" variant="outline"
                                disabled={riprovaId === u.id}
                                onClick={() => riprova(u)}>
                          {riprovaId === u.id ? "Verifica in corso…" : "Riprova accesso"}
                        </Button>
                      )}
                      <Button size="sm" variant="destructive" onClick={() => apriElimina(u)}>
                        Elimina
                      </Button>
                    </div>
                  </td>
                </tr>
                {assegnaId === u.id && (
                  <tr className="border-t border-border bg-muted/20">
                    <td colSpan={6} className="py-3 px-4">
                      <FormAssegna utenza={u} onFatto={async () => { setAssegnaId(null); await onRicarica(); }} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={daEliminare !== null}
        title="Elimina utenza"
        message={messaggioElimina(daEliminare?.agenzie.length ?? 0)}
        confirmLabel="Elimina utenza"
        busy={busy}
        error={errDialogo}
        onConfirm={elimina}
        onCancel={() => setDaEliminare(null)}
      />

      <ConfirmDialog
        open={daTogliere !== null}
        title="Scollega agenzia"
        message={`${daTogliere?.nome ?? ""} resterà senza utenza ${daTogliere?.u.fornitore ?? ""}: le sue pratiche si fermano. Confermi?`}
        confirmLabel="Scollega"
        busy={busy}
        error={errDialogo}
        onConfirm={togli}
        onCancel={() => setDaTogliere(null)}
      />
    </>
  );
}
